import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { FieldValues, SubmitHandler, useForm } from "react-hook-form";
import { TextInput } from "../../components/TextInput";
import { gql, useMutation, useQuery } from "@apollo/client";
import { PARKERS } from "./Parkers";
import { toast } from "react-toastify";
import { Vehicles } from "../Vehicles/Vehicles";
import { Transactions } from "../Transactions/Transactions";
import { QRCodeSVG } from "qrcode.react";
import { Testing } from "../Testing/Testing";
import { Permanents } from "../Permanent/Permanents";
import { Payment } from "./Payment";
import { GetParkerQuery, Parker as ParkerType } from "../../gql/graphql";
import AuditInfo from "../../components/AuditInfo";
import { DeleteModal } from "../../components/DeleteModal";

const GET = gql`
  query GetParker($id: UUID!) {
    parkers(where: { id: { eq: $id } }) {
      items {
        id
        email
        firstName
        lastName
        mobile
        createdUtc
        isActive
        isEmailVerified
        qrCode
        uid
        streetNumber
        streetName
        suburb
        state
        postcode
        notificationType
        agreeTermsConditions
        stripeCustomerId
        stripeMetadata
        stripeDefaultPaymentMethodId
        isEmailVerified
        isProfileComplete
      }
    }
  }
`;

const UPDATE = gql`
  mutation UpdateParker($parker: ParkerInput!) {
    updateParker(parker: $parker) {
      id
      email
      firstName
      lastName
      mobile
      createdUtc
      isActive
      isEmailVerified
      qrCode
    }
  }
`;

const QRCODE = gql`
  mutation ReissueQrCode($parker: ParkerInput!) {
    reissueQrCode(parker: $parker) {
      id
      email
      firstName
      lastName
      mobile
      createdUtc
      isActive
      isEmailVerified
      qrCode
    }
  }
`;

export const Parker = () => {
  let { parkerId, tab } = useParams();
  const navigate = useNavigate();

  const [parker, setParker] = useState<
    NonNullable<NonNullable<GetParkerQuery["parkers"]>["items"]>[number] | null
  >(null);
  const { data } = useQuery<GetParkerQuery>(GET, {
    variables: {
      id:
        parkerId === "new" ? "00000000-0000-0000-0000-000000000000" : parkerId,
    },
  });

  const [updateParker, { loading: updating }] = useMutation<
    {
      updateParker: ParkerType;
    },
    {
      parker: ParkerType;
    }
  >(UPDATE, {
    refetchQueries: parkerId === "new" ? [{ query: PARKERS }] : [],
    awaitRefetchQueries: true,
  }); // [ updateCompany, { data, loading, error }]
  const [reissueQrCode] = useMutation<
    {
      reissueQrCode: ParkerType;
    },
    {
      parker: Pick<ParkerType, "id" | "isActive" | "isEmailVerified">;
    }
  >(QRCODE);

  useEffect(() => {
    if (parkerId === "new") {
      setParker({
        id: "00000000-0000-0000-0000-000000000000",
        email: "",
        firstName: "",
        lastName: "",
        mobile: "",
        createdUtc: null,
        isActive: true,
        isEmailVerified: false,
      });
    }
    if (data?.parkers?.items?.length === 1) {
      setParker(data.parkers.items[0]);
    }
  }, [data, parkerId]);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();

  const onSubmit: SubmitHandler<FieldValues> = (data) => {
    updateParker({
      variables: {
        parker: {
          ...(data as ParkerType),
          id: parker?.id!,
          isActive: parker?.isActive!,
          isEmailVerified: parker?.isEmailVerified!,
        },
      },
    }).then((x) => {
      if (x.data?.updateParker) {
        toast(`${parker?.email} saved`, {
          type: toast.TYPE.SUCCESS,
          autoClose: 3000,
        });
        navigate("/parkers");
      }
    });
  };

  const onReissueQrCode = () => {
    reissueQrCode({
      variables: {
        parker: {
          id: parker?.id!,
          isActive: parker?.isActive!,
          isEmailVerified: parker?.isEmailVerified!,
        },
      },
    }).then((x) => {
      if (x.data?.reissueQrCode) {
        toast(`New QR Code issued`, {
          type: toast.TYPE.SUCCESS,
          autoClose: 3000,
        });
      }
    });
  };

  return (
    <>
      {parker && (
        <>
          <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center pb-2">
            <h1 className="h2">
              Parker {parker.email ? " - " + parker.email : ""}
              {!parker.isActive && (
                <span className="badge bg-secondary ms-2">Inactive</span>
              )}
            </h1>
          </div>
          <AuditInfo
            entity={
              parker as {
                createdBy?: string;
                createdUtc?: string | null;
                modifiedBy?: string;
                modifiedUtc?: string | null;
              }
            }
          />

          <ul className="nav nav-tabs mb-3">
            <li className="nav-item">
              <Link
                className={`nav-link ${!tab ? "active" : ""}`}
                to={`/parkers/${parker.id}`}
              >
                Details
              </Link>
            </li>

            {parker.id !== "00000000-0000-0000-0000-000000000000" && (
              <>
                <li className="nav-item">
                  <Link
                    className={`nav-link ${tab === "qr-code" ? "active" : ""}`}
                    to={`/parkers/${parker.id}/qr-code`}
                  >
                    QR Code
                  </Link>
                </li>

                <li className="nav-item">
                  <Link
                    className={`nav-link ${tab === "payment" ? "active" : ""}`}
                    to={`/parkers/${parker.id}/payment`}
                  >
                    Payment
                  </Link>
                </li>

                <li className="nav-item">
                  <Link
                    className={`nav-link ${tab === "vehicles" ? "active" : ""}`}
                    to={`/parkers/${parker.id}/vehicles`}
                  >
                    Vehicles
                  </Link>
                </li>

                <li className="nav-item">
                  <Link
                    className={`nav-link ${
                      tab === "transactions" ? "active" : ""
                    }`}
                    to={`/parkers/${parker.id}/transactions`}
                  >
                    Transactions
                  </Link>
                </li>

                <li className="nav-item">
                  <Link
                    className={`nav-link ${
                      tab === "permanent" ? "active" : ""
                    }`}
                    to={`/parkers/${parker.id}/permanent`}
                  >
                    Permanent
                  </Link>
                </li>

                <li className="nav-item">
                  <Link
                    className={`nav-link text-danger ${
                      tab === "testing" ? "active" : ""
                    }`}
                    to={`/parkers/${parker.id}/testing`}
                  >
                    Testing
                  </Link>
                </li>
              </>
            )}
          </ul>

          {!tab && (
            <form
              onSubmit={handleSubmit(onSubmit)}
              className={
                "needs-validation" +
                (Object.keys(errors).length > 0 ? " was-validated" : "")
              }
              noValidate
            >
              <div className="row">
                <div className="col-md">
                  <TextInput
                    disabled={!!parker.createdUtc}
                    name="email"
                    label="Email"
                    defaultValue={parker.email ?? ""}
                    placeholder="Email"
                    register={register}
                    config={{
                      required:
                        parker.id === "00000000-0000-0000-0000-000000000000",
                    }}
                    errors={errors}
                  />
                </div>
              </div>

              <div className="row">
                <div className="col-md">
                  <TextInput
                    name="firstName"
                    label="First Name"
                    defaultValue={parker.firstName ?? ""}
                    placeholder="First Name"
                    register={register}
                    config={{ required: false }}
                    errors={errors}
                  />
                </div>
                <div className="col-md">
                  <TextInput
                    name="lastName"
                    label="Last Name"
                    defaultValue={parker.lastName ?? ""}
                    placeholder="Last Name"
                    register={register}
                    config={{ required: false }}
                    errors={errors}
                  />
                </div>
              </div>

              <div className="row">
                <div className="col-md">
                  <TextInput
                    name="mobile"
                    label="Mobile"
                    defaultValue={parker.mobile ?? ""}
                    placeholder="Mobile"
                    register={register}
                    config={{ required: false }}
                    errors={errors}
                  />
                </div>
              </div>

              <div className="row">
                <div className="col-md">
                  <label className="form-label">Notification Preferences</label>
                  <div>
                    {parker.notificationType?.toLowerCase().includes("email") && (
                      <span className="badge bg-primary me-2">
                        <span style={{marginRight: '4px'}}>
                          <svg xmlns="http://www.w3.org/2000/svg" height="14" viewBox="0 0 24 24" width="14" fill="white" style={{verticalAlign: 'text-bottom'}}>
                            <path d="M0 0h24v24H0z" fill="none"/>
                            <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
                          </svg>
                        </span>
                        Email
                      </span>
                    )}
                    {parker.notificationType?.toLowerCase().includes("sms") && (
                      <span className="badge bg-success me-2">
                        <span style={{marginRight: '4px'}}>
                          <svg xmlns="http://www.w3.org/2000/svg" height="14" viewBox="0 0 24 24" width="14" fill="white" style={{verticalAlign: 'text-bottom'}}>
                            <path d="M0 0h24v24H0z" fill="none"/>
                            <path d="M20 2H4c-1.1 0-1.99.9-1.99 2L2 22l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zM9 11H7V9h2v2zm4 0h-2V9h2v2zm4 0h-2V9h2v2z"/>
                          </svg>
                        </span>
                        SMS
                      </span>
                    )}
                    {!parker.notificationType && (
                      <span className="text-muted">No notification preferences set</span>
                    )}
                  </div>
                </div>
              </div>

              <div className="mt-3 mb-3 d-flex justify-content-between">
                <div>
                  <input
                    disabled={updating}
                    className="btn btn-primary"
                    type="submit"
                    value="Save"
                  />{" "}
                  <Link to={"/parkers"} className="btn btn-link">
                    Cancel
                  </Link>
                </div>
                {parkerId !== "new" && !parker.isActive && (
                  <button
                    type="button"
                    className="btn btn-warning"
                    onClick={() => {
                      updateParker({
                        variables: {
                          parker: {
                            id: parker.id,
                            email: parker.email,
                            firstName: parker.firstName,
                            lastName: parker.lastName,
                            mobile: parker.mobile,
                            isActive: true,
                            isEmailVerified: parker.isEmailVerified,
                          } as ParkerType,
                        },
                      }).then((x) => {
                        if (x.data?.updateParker) {
                          toast("Parker restored", {
                            type: toast.TYPE.SUCCESS,
                            autoClose: 3000,
                          });
                          navigate("/parkers");
                        }
                      });
                    }}
                  >
                    Restore
                  </button>
                )}
                {parkerId !== "new" && parker.isActive && (
                  <DeleteModal
                    title="Parker"
                    onConfirm={() => {
                      updateParker({
                        variables: {
                          parker: {
                            id: parker.id,
                            email: parker.email,
                            firstName: parker.firstName,
                            lastName: parker.lastName,
                            mobile: parker.mobile,
                            isActive: false,
                            isEmailVerified: parker.isEmailVerified,
                          } as ParkerType,
                        },
                      }).then((x) => {
                        if (x.data?.updateParker) {
                          toast("Parker deleted", {
                            type: toast.TYPE.SUCCESS,
                            autoClose: 3000,
                          });
                          navigate("/parkers");
                        }
                      });
                    }}
                  >
                    <dl className="mb-0">
                      {parker.email && (
                        <>
                          <dt>Email</dt>
                          <dd>{parker.email}</dd>
                        </>
                      )}
                      {(parker.firstName || parker.lastName) && (
                        <>
                          <dt>Name</dt>
                          <dd>{parker.firstName} {parker.lastName}</dd>
                        </>
                      )}
                      {parker.mobile && (
                        <>
                          <dt>Mobile</dt>
                          <dd>{parker.mobile}</dd>
                        </>
                      )}
                    </dl>
                  </DeleteModal>
                )}
              </div>
            </form>
          )}

          {tab === "qr-code" && (
            <>
              <div className="mb-3">
                <button
                  className="btn btn-primary"
                  type="button"
                  onClick={onReissueQrCode}
                >
                  Reissue QR Code
                </button>
              </div>

              {parker?.qrCode && parker?.uid && (
                <QRCodeSVG
                  value={parker.uid}
                  size={256}
                  bgColor={"#ffffff"}
                  fgColor={"#000000"}
                  level={"L"}
                  includeMargin={false}
                />
              )}
            </>
          )}

          {tab === "payment" && parker.uid && <Payment parker={parker} />}
          {tab === "testing" && parker.uid && <Testing qrCode={parker.uid} />}
          {tab === "vehicles" && <Vehicles parkerId={parker.id} />}
          {tab === "transactions" && <Transactions parkerId={parker.id} />}
          {tab === "permanent" && <Permanents parkerId={parker.id} />}
        </>
      )}
    </>
  );
};
